/**
 * Log Batcher — ships Odin Sight client logs to MarketData when the user is
 * whitelisted for remote log shipping. Mirrors the shape of errorReportService
 * but runs independently:
 *
 * - Gated by setEnabled(true) — which the logger calls after login when the
 *   server returns RemoteLogging=true on the token response.
 * - Batches every 30s or 200 events, whichever comes first.
 * - Persists the pending queue to localStorage (survives reload / offline).
 * - Auto-disables on 403 from the server (revocation propagates within one batch).
 * - Drops the queue — never stores anything — while disabled.
 */

import { settings, parsedMarketdataUrls } from "../data/Services/settings";
import { PRODUCT_VERSION } from "../constants/product";
import { scrub } from "./piiScrubber";

const STORAGE_KEY = "odin-sight-log-batcher-queue";
const SESSION_ID_KEY = "odin-sight-log-batcher-session";

class LogBatcher {
  constructor() {
    this.enabled = false;
    this.authProvider = null;
    this.queue = [];
    this.sending = false;
    this.batchTimer = null;
    this.retryCount = 0;

    this.batchSize = 200;
    this.batchInterval = 30000;
    this.maxQueueSize = 1000;
    this.maxRetries = 3;
    this.baseRetryDelay = 5000;

    this.sessionId = this.loadOrCreateSession();
    this.loadQueue();

    if (typeof window !== "undefined") {
      window.addEventListener("beforeunload", () => this.flush());
    }
  }

  /**
   * Toggle shipping. When turning off, the in-memory and persisted queues
   * are discarded — shipping is opt-in and we should not retain old events
   * when revoked.
   */
  setEnabled(enabled) {
    const was = this.enabled;
    this.enabled = !!enabled;
    if (!this.enabled && was) {
      this.queue = [];
      this.saveQueue();
      this.stopBatchTimer();
      console.log("[LogBatcher] Shipping disabled, queue cleared");
    }
    if (this.enabled && !was) {
      this.startBatchTimer();
      console.log("[LogBatcher] Shipping enabled");
    }
  }

  isEnabled() {
    return this.enabled;
  }

  /**
   * Called from marketdata.jsx after login — same pattern errorReportService uses.
   */
  setAuthProvider(provider) {
    this.authProvider = provider;
  }

  /**
   * Enqueue one log event. Safe to call regardless of enabled state — entries
   * are dropped when disabled.
   */
  push(event) {
    if (!this.enabled) return;
    if (!event || typeof event !== "object") return;

    let sanitized;
    try {
      sanitized = {
        timestamp: event.timestamp || new Date().toISOString(),
        level: event.level,
        category: event.category,
        message:
          typeof event.message === "string"
            ? event.message
            : String(event.message ?? ""),
        details: event.details == null ? null : scrub(event.details),
        url: typeof window !== "undefined" ? window.location.href : null,
        userAgent:
          typeof navigator !== "undefined" ? navigator.userAgent : null,
        appVersion: PRODUCT_VERSION,
      };
      sanitized.message = scrub(sanitized.message);
    } catch (err) {
      console.warn("[LogBatcher] Failed to sanitize event", err);
      return;
    }

    if (this.queue.length >= this.maxQueueSize) {
      this.queue.shift();
    }

    this.queue.push(sanitized);

    if (this.queue.length >= this.batchSize) {
      this.flush();
    } else {
      this.saveQueue();
    }
  }

  startBatchTimer() {
    this.stopBatchTimer();
    this.batchTimer = setInterval(() => {
      if (this.queue.length > 0) this.flush();
    }, this.batchInterval);
  }

  stopBatchTimer() {
    if (this.batchTimer) {
      clearInterval(this.batchTimer);
      this.batchTimer = null;
    }
  }

  async flush() {
    if (!this.enabled || this.sending || this.queue.length === 0) return;

    this.sending = true;
    const batch = this.queue.slice(0, this.batchSize);

    try {
      const response = await this.send(batch);

      if (response.status === 403) {
        // Whitelist revoked — stop shipping immediately and notify the logger.
        console.warn("[LogBatcher] Server returned 403 — disabling shipping");
        this.setEnabled(false);
        this._emitDisabledFromServer();
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      this.queue.splice(0, batch.length);
      this.saveQueue();
      this.retryCount = 0;
    } catch (err) {
      console.warn("[LogBatcher] Batch send failed:", err?.message || err);
      this.scheduleRetry();
    } finally {
      this.sending = false;
    }
  }

  async send(batch) {
    const broker = settings.marketdataUrl?.broker || "TZ";
    const urls = parsedMarketdataUrls[broker];
    if (!urls || urls.length === 0) {
      throw new Error(`MarketData URL not configured for broker ${broker}`);
    }

    const apiUrl = `${urls[0]}/api/logs/batch`;
    const authData = this.authProvider ? this.authProvider() : null;
    if (!authData?.token) {
      throw new Error("No auth token — cannot ship logs");
    }

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${authData.token}`,
    };

    return fetch(apiUrl, {
      method: "POST",
      headers,
      body: JSON.stringify({ sessionId: this.sessionId, events: batch }),
    });
  }

  scheduleRetry() {
    if (this.retryCount >= this.maxRetries) {
      this.retryCount = 0;
      return;
    }
    this.retryCount++;
    const delay = this.baseRetryDelay * Math.pow(2, this.retryCount - 1);
    setTimeout(() => this.flush(), delay);
  }

  saveQueue() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.queue));
    } catch {
      // localStorage full or disabled — nothing to do, in-memory queue still works.
    }
  }

  loadQueue() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) this.queue = JSON.parse(raw) || [];
    } catch {
      this.queue = [];
    }
  }

  loadOrCreateSession() {
    try {
      const existing = sessionStorage.getItem(SESSION_ID_KEY);
      if (existing) return existing;
    } catch {
      // sessionStorage unavailable — fall through.
    }
    const fresh = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    try {
      sessionStorage.setItem(SESSION_ID_KEY, fresh);
    } catch {
      // ignore
    }
    return fresh;
  }

  _emitDisabledFromServer() {
    if (
      typeof window !== "undefined" &&
      typeof window.dispatchEvent === "function"
    ) {
      window.dispatchEvent(new CustomEvent("remote-log-disabled"));
    }
  }
}

const logBatcher = new LogBatcher();
export default logBatcher;
export { LogBatcher };
